"use client";
import * as React from "react";
import { DataTable, type Column } from "../DataTable";
import { PageHeader, StatCard, Card2 } from "../ModuleHelpers";
import { useERP, formatBDT, formatNum, type ID, type Customer } from "@/lib/erp/store";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CreditCard, ShieldBan, ShieldCheck, Settings2, Eye } from "lucide-react";

export function CreditControlModule() {
  const erp = useERP();
  const { toast } = useToast();
  const [adjustId, setAdjustId] = React.useState<string | null>(null);

  const rows = erp.customers.map(c => ({
    id: c.id, code: c.code, name: c.name, type: c.type,
    limit: c.creditLimit, outstanding: c.outstanding, util: Math.round((c.outstanding / c.creditLimit) * 100),
    creditDays: c.creditDays, status: c.status, score: c.status === "Blocked" ? 30 : c.status === "Watch" ? 65 : 90,
  }));

  const handleToggleBlock = (c: Customer) => {
    const newStatus: Customer["status"] = c.status === "Blocked" ? "Active" : "Blocked";
    erp.updateCustomer(c.id, { status: newStatus });
    toast({
      title: newStatus === "Blocked" ? "Customer Blocked" : "Customer Released",
      description: `${c.name} is now ${newStatus}. ${newStatus === "Blocked" ? "New SOs will fail credit check." : "SOs can proceed normally."}`,
      variant: newStatus === "Blocked" ? "destructive" : "default",
    });
  };

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="Credit Control" desc="Receivables intelligence, credit scoring and limit management." icon={CreditCard} />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Total AR" value={formatBDT(erp.customers.reduce((s, c) => s + c.outstanding, 0))} tone="text-destructive" />
        <StatCard label="Credit Limit" value={formatBDT(erp.customers.reduce((s, c) => s + c.creditLimit, 0))} />
        <StatCard label="Avg Utilization" value={`${Math.round(erp.customers.reduce((s, c) => s + (c.outstanding / c.creditLimit) * 100, 0) / erp.customers.length)}%`} />
        <StatCard label="Blocked Customers" value={formatNum(erp.customers.filter(c => c.status === "Blocked").length)} tone="text-destructive" />
      </div>
      <Card2 title="Credit Scorecard">
        <DataTable
          columns={[
            { key: "code", header: "Code", sortable: true, render: r => <span className="font-mono text-xs">{r.code}</span> },
            { key: "name", header: "Customer", sortable: true },
            { key: "type", header: "Type" },
            { key: "limit", header: "Credit Limit", align: "right", render: r => formatBDT(r.limit) },
            { key: "outstanding", header: "Outstanding", align: "right", sortable: true, render: r => <span className={r.outstanding > 0 ? "text-destructive font-medium" : ""}>{formatBDT(r.outstanding)}</span> },
            { key: "util", header: "Util %", align: "right", render: r => <span className={r.util > 80 ? "text-destructive font-semibold" : ""}>{r.util}%</span> },
            { key: "score", header: "Score", align: "right", sortable: true, render: r => <span className={`inline-flex rounded-md px-2 py-0.5 text-xs ${r.score >= 85 ? "bg-primary text-primary-foreground" : r.score >= 60 ? "bg-secondary" : "bg-destructive text-white"}`}>{r.score}</span> },
            { key: "status", header: "Status", render: r => <Badge variant={r.status === "Active" ? "default" : r.status === "Blocked" ? "destructive" : "secondary"}>{r.status}</Badge> },
            { key: "actions", header: "Actions", align: "right", render: r => {
              const c = erp.customers.find(x => x.id === r.id);
              if (!c) return null;
              return (
                <div className="flex justify-end gap-1" onClick={e => e.stopPropagation()}>
                  <Button size="sm" variant="ghost" className="size-7 p-0" title="View AR History" onClick={() => toast({ title: c.name, description: `AR: ${formatBDT(c.outstanding)} / Limit: ${formatBDT(c.creditLimit)} · ${c.creditDays} days credit` })}><Eye className="size-3.5" /></Button>
                  <Button size="sm" variant="ghost" className="size-7 p-0" title="Adjust Limit" onClick={() => setAdjustId(r.id)}><Settings2 className="size-3.5" /></Button>
                  <Button size="sm" variant="ghost" className={`size-7 p-0 ${c.status === "Blocked" ? "text-emerald-600" : "text-destructive"}`} title={c.status === "Blocked" ? "Release Hold" : "Block Customer"} onClick={() => handleToggleBlock(c)}>
                    {c.status === "Blocked" ? <ShieldCheck className="size-3.5" /> : <ShieldBan className="size-3.5" />}
                  </Button>
                </div>
              );
            }},
          ] as Column<typeof rows[number]>[]}
          data={rows}
          rowKey={r => r.id}
          searchKeys={["code", "name", "type"]}
        />
      </Card2>

      <AdjustLimitDialog customerId={adjustId} onOpenChange={(v) => !v && setAdjustId(null)} />
    </div>
  );
}

function AdjustLimitDialog({ customerId, onOpenChange }: { customerId: string | null; onOpenChange: (v: boolean) => void }) {
  const erp = useERP();
  const { toast } = useToast();
  const customer = erp.customers.find(c => c.id === customerId);
  const [limit, setLimit] = React.useState("");
  const [days, setDays] = React.useState("");
  React.useEffect(() => { if (customer) { setLimit(String(customer.creditLimit)); setDays(String(customer.creditDays)); } }, [customerId]);

  if (!customer) return null;

  return (
    <Dialog open={!!customerId} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Settings2 className="size-5" /> Adjust Credit Terms</DialogTitle>
          <DialogDescription>Update credit limit and payment days for {customer.name}.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-md border bg-muted/30 p-3 text-sm space-y-1">
            <div className="flex justify-between"><span className="text-muted-foreground">Customer:</span><span className="font-medium">{customer.name}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Current Outstanding:</span><span className="font-medium text-destructive">{formatBDT(customer.outstanding)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Utilization:</span><span className="font-medium">{Math.round((customer.outstanding / customer.creditLimit) * 100)}%</span></div>
          </div>
          <div>
            <Label htmlFor="lim">New Credit Limit (৳)</Label>
            <Input id="lim" type="number" value={limit} onChange={e => setLimit(e.target.value)} autoFocus />
          </div>
          <div>
            <Label htmlFor="days">Credit Days</Label>
            <Input id="days" type="number" value={days} onChange={e => setDays(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => {
            const newLimit = Number(limit) || 0;
            const newDays = Number(days) || 0;
            if (newLimit <= 0) { toast({ title: "Invalid limit", variant: "destructive" }); return; }
            erp.updateCustomer(customer.id, { creditLimit: newLimit, creditDays: newDays });
            toast({ title: "Credit Terms Updated", description: `${customer.name}: limit ${formatBDT(newLimit)}, ${newDays} days.` });
            onOpenChange(false);
          }}><Settings2 className="size-4 mr-1" /> Update</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
